import { useState, type FormEvent } from "react";
import { brand } from "@/mocks/baoliaoData";

const FORM_URL = "https://readdy.ai/api/form/dadvokjep053ijub1640";

type Status =
  | { type: "idle" }
  | { type: "loading" }
  | { type: "success"; text: string }
  | { type: "error"; text: string };

export default function SubscribeSection() {
  const [status, setStatus] = useState<Status>({ type: "idle" });

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honey = String(formData.get("contact_alt") || "").trim();
    if (honey) {
      setStatus({ type: "success", text: "订阅成功！热瓜推送已安排，请留意邮箱。" });
      return;
    }

    setStatus({ type: "loading" });
    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      const v = String(value).trim();
      if (v && key !== "contact_alt") params.append(key, v);
    });

    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: any = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }
      const serverMsg =
        (parsed?.meta?.message as string) ||
        (parsed?.message as string) ||
        (parsed?.meta?.detail as string) ||
        "";
      const serverDetail = (parsed?.meta?.detail as string) || "";
      const isSpam = /spam/i.test(serverMsg) || /spam/i.test(serverDetail) || /form data is spam/i.test(responseText);
      const codeOk = parsed?.code === "OK";
      if (response.ok && codeOk && !isSpam) {
        form.reset();
        setStatus({ type: "success", text: "订阅成功！今天的热瓜，明天第一时间送到你邮箱。" });
      } else {
        setStatus({
          type: "error",
          text: serverMsg || serverDetail || "提交失败，请稍后再试或检查邮箱格式。",
        });
      }
    } catch {
      setStatus({ type: "error", text: "网络异常，提交失败，请稍后重试。" });
    }
  };

  return (
    <section id="subscribe" className="py-10 md:py-16 bg-background-100/70 border-t border-background-200/60">
      <div className="max-w-[1240px] mx-auto px-4 md:px-6">
        <div className="rounded-3xl overflow-hidden bg-foreground-950 text-background-100">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            {/* left info */}
            <div className="p-7 md:p-10 flex flex-col justify-between gap-8">
              <div>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-500 text-background-50 px-3 py-1 text-xs font-semibold">
                  <i className="ri-notification-3-line text-xs"></i>
                  实时订阅推送
                </span>
                <h2 className="font-heading font-extrabold text-2xl md:text-3xl text-background-50 mt-4 leading-snug">
                  别等明天，今天的热瓜现在就知道
                </h2>
                <p className="mt-3 text-sm text-background-100/70 leading-relaxed">
                  留下邮箱，我们会把每日精选的实时爆料与热点追踪进度打包推送给你。不刷屏、不打扰，只在值得看的时候出现。
                </p>
              </div>
              <div className="flex flex-col gap-3 text-sm text-background-100/80">
                <p className="flex items-center gap-3">
                  <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100/10">
                    <i className="ri-mail-line"></i>
                  </span>
                  商务 / 投稿 / 线索：hub@baoliao.example
                </p>
                <p className="flex items-center gap-3">
                  <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100/10">
                    <i className="ri-time-line"></i>
                  </span>
                  内容团队在线：每日 9:00 - 23:00
                </p>
              </div>
            </div>

            {/* form */}
            <div className="p-7 md:p-10 bg-background-100/5 border-l border-background-100/10 flex flex-col justify-center">
              <form id="newsletter-form" data-readdy-form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <div>
                  <label htmlFor="nl-name" className="block text-xs font-semibold text-background-100/70 mb-1.5">
                    怎么称呼你（可选）
                  </label>
                  <input
                    id="nl-name"
                    name="nickname"
                    type="text"
                    maxLength={20}
                    placeholder="例如：猹友小张"
                    className="w-full rounded-lg border border-background-100/15 bg-background-100/5 px-4 py-3 text-sm text-background-50 placeholder:text-background-100/40 outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400/50 transition-colors"
                  />
                </div>
                <div>
                  <label htmlFor="nl-email" className="block text-xs font-semibold text-background-100/70 mb-1.5">
                    接收推送的邮箱 <span className="text-primary-400">*</span>
                  </label>
                  <input
                    id="nl-email"
                    name="email"
                    type="email"
                    required
                    placeholder="you@example.com"
                    className="w-full rounded-lg border border-background-100/15 bg-background-100/5 px-4 py-3 text-sm text-background-50 placeholder:text-background-100/40 outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400/50 transition-colors"
                  />
                </div>

                {/* honeypot anti-spam */}
                <div className="hp-field" aria-hidden="true">
                  <label htmlFor="contact_alt">Contact</label>
                  <input
                    id="contact_alt"
                    name="contact_alt"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    readOnly
                  />
                </div>

                <button
                  type="submit"
                  disabled={status.type === "loading"}
                  className="mt-1 inline-flex items-center justify-center gap-2 rounded-lg bg-primary-500 text-background-50 px-5 py-3 text-sm font-semibold hover:bg-primary-400 disabled:opacity-60 transition-colors whitespace-nowrap"
                >
                  {status.type === "loading" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      提交中…
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line"></i>
                      订阅每日热点推送
                    </>
                  )}
                </button>

                {status.type === "success" && (
                  <p className="flex items-start gap-2 rounded-lg bg-emerald-500/15 text-emerald-200 px-4 py-3 text-xs leading-relaxed">
                    <i className="ri-checkbox-circle-line text-base mt-px"></i>
                    {status.text}
                  </p>
                )}
                {status.type === "error" && (
                  <p className="flex items-start gap-2 rounded-lg bg-primary-500/15 text-primary-200 px-4 py-3 text-xs leading-relaxed">
                    <i className="ri-error-warning-line text-base mt-px"></i>
                    {status.text}
                  </p>
                )}
                <p className="text-[11px] text-background-100/45 leading-relaxed">
                  订阅即表示你同意我们仅将邮箱用于发送 {brand} 内容推送，可随时一键退订。
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}